import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import HeaderTop from '../../components/HeaderTop/HeaderTop';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle';
import CtaSection from '../../components/CtaSection/CtaSection';
import MapSection from '../../components/MapSection/MapSection';
import FooterS2 from '../../components/FooterS2/FooterS2';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Logo from '../../images/logo.png';
import TransportBg from '../../images/downloaded/transport-bg.jpg';
import TextBg from '../../images/text-img.jpg';

const SEARCH_MODES = [
  {
    label: 'Bill of Lading',
    key: 'ladingNo',
    placeholder: 'Enter bill of lading number (format: 2 letters + 9 digits)',
  },
  {
    label: 'Container ID',
    key: 'containerId',
    placeholder: 'Enter container ID (format: 4 letters + 7 digits)',
  },
  {
    label: 'Booking Number',
    key: 'bookingNo',
    placeholder: 'Enter booking number (format: 2 letters + 9 digits)',
  },
];

const TRACKING_API = 'https://admin.mcpl.pk/api/trackings';
const TRACKING_PLATFORM = 'ferryGoldShipping';

const buildValidatedDate = (
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
) => {
  const parsedDate = new Date(year, month - 1, day, hour, minute, second);

  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day ||
    parsedDate.getHours() !== hour ||
    parsedDate.getMinutes() !== minute ||
    parsedDate.getSeconds() !== second
  ) {
    return null;
  }

  return parsedDate;
};

const safeDate = (dateStr) => {
  if (!dateStr) {
    return null;
  }

  if (dateStr instanceof Date) {
    return Number.isNaN(dateStr.getTime()) ? null : dateStr;
  }

  const input = String(dateStr).trim();
  if (!input) {
    return null;
  }

  const ymdMatch = input.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (ymdMatch) {
    const [, year, month, day, hour = '00', minute = '00', second = '00'] =
      ymdMatch;
    return buildValidatedDate(
      Number(year),
      Number(month),
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  const dmyMatch = input.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dmyMatch) {
    const [, day, month, year, hour = '00', minute = '00', second = '00'] =
      dmyMatch;
    return buildValidatedDate(
      Number(year),
      Number(month),
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  const normalized = input.includes('T') ? input : input.replace(' ', 'T');
  const parsedDate = new Date(normalized);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDate = (dateStr) => {
  const parsed = safeDate(dateStr);
  if (!parsed) {
    return dateStr || '--';
  }

  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeTimeline = (trackingDetails = []) => {
  return [...trackingDetails].sort((a, b) => {
    const dateA = safeDate(a.date);
    const dateB = safeDate(b.date);

    if (!dateA && !dateB) {
      return 0;
    }

    if (!dateA) {
      return 1;
    }

    if (!dateB) {
      return -1;
    }

    return dateA.getTime() - dateB.getTime();
  });
};

const normalizeText = (value = '') =>
  String(value).toLowerCase().replace(/\s+/g, ' ').trim();

const includesAny = (value, keywords = []) =>
  keywords.some((keyword) => value.includes(keyword));

const isCompletedMilestone = (
  eventName = '',
  location = '',
  destination = '',
) => {
  const eventValue = normalizeText(eventName);

  if (includesAny(eventValue, ['delivered', 'delivery completed'])) {
    return true;
  }

  const arrivalLikeEvent = includesAny(eventValue, [
    'arrival',
    'arrive',
    'received at port',
    'container received',
    'discharge',
    'discharged',
  ]);

  if (!arrivalLikeEvent) {
    return false;
  }

  // Arrival/discharge/received events are considered complete milestones.
  // Destination text can vary (e.g., Karachi vs Qasim Port), so don't block completion on exact string match.
  return arrivalLikeEvent;
};

const getStatusTone = (statusValue = '', location = '', destination = '') => {
  const value = statusValue.toLowerCase();

  if (isCompletedMilestone(statusValue, location, destination)) {
    return 'green';
  }

  if (value.includes('arrived')) {
    return 'green';
  }

  if (value.includes('in transit')) {
    return 'blue';
  }

  if (value.includes('loaded') || value.includes('confirmed')) {
    return 'blue';
  }

  if (value.includes('gate') || value.includes('terminal')) {
    return 'gray';
  }

  return '';
};

const getShipmentStatus = (attributes = {}, latestEvent = null) => {
  const now = Date.now();
  const departureDate = safeDate(attributes.receiptDate);
  const arrivalDate = safeDate(attributes.deliveryDate);

  if (arrivalDate && now >= arrivalDate.getTime()) {
    return 'Arrived';
  }

  if (departureDate && now >= departureDate.getTime()) {
    return 'In Transit';
  }

  if (latestEvent?.event) {
    return isCompletedMilestone(
      latestEvent.event,
      latestEvent.location,
      attributes.destination,
    )
      ? 'Arrived'
      : 'In Transit';
  }

  return 'In Transit';
};

const getProgress = (
  eventName = '',
  timelineLength = 0,
  location = '',
  destination = '',
) => {
  const value = eventName.toLowerCase();

  if (isCompletedMilestone(eventName, location, destination)) {
    return 100;
  }

  if (
    includesAny(value, ['arrival', 'received at port', 'container received'])
  ) {
    return 100;
  }

  if (value.includes('arrive') || value.includes('discharge')) {
    return 90;
  }

  if (
    value.includes('transit') ||
    value.includes('depart') ||
    value.includes('vessel')
  ) {
    return 68;
  }

  if (value.includes('loaded')) {
    return 50;
  }

  if (value.includes('gate') || value.includes('terminal')) {
    return 32;
  }

  if (value.includes('book') || value.includes('confirm')) {
    return 18;
  }

  if (!timelineLength) {
    return 0;
  }

  return Math.min(95, Math.max(10, timelineLength * 16));
};

const formatShortDate = (dateStr) => {
  const parsed = safeDate(dateStr);
  if (!parsed) {
    return dateStr || '--';
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
  });
};

const isPendingMilestone = (dateStr = '', eventName = '') => {
  const parsedDate = safeDate(dateStr);
  const eventValue = normalizeText(eventName);

  if (includesAny(eventValue, ['expected', 'estimated', 'eta'])) {
    return true;
  }

  if (!parsedDate) {
    return false;
  }

  return parsedDate.getTime() > Date.now();
};

const TrackingPage = () => {
  const [mode, setMode] = useState(SEARCH_MODES[0]);
  const [trackingValue, setTrackingValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [lastSearchContext, setLastSearchContext] = useState({
    key: SEARCH_MODES[0].key,
    label: SEARCH_MODES[0].label,
    value: '',
  });
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const ordersList = useMemo(
    () => responseData?.orders?.data || [],
    [responseData],
  );

  const activeOrder = useMemo(() => {
    if (!ordersList.length) {
      return null;
    }

    if (!selectedOrderId) {
      return ordersList[0];
    }

    return (
      ordersList.find((orderItem) => orderItem.id === selectedOrderId) ||
      ordersList[0]
    );
  }, [ordersList, selectedOrderId]);

  const orderAttributes = activeOrder?.attributes || null;
  const timeline = useMemo(
    () => normalizeTimeline(orderAttributes?.trackingDetails || []),
    [orderAttributes],
  );
  const latestEvent = timeline.length ? timeline[timeline.length - 1] : null;
  const activeStatus = getShipmentStatus(orderAttributes || {}, latestEvent);
  const activeProgress = getProgress(
    activeStatus,
    timeline.length,
    latestEvent?.location,
    orderAttributes?.destination,
  );
  const isContainerMode = lastSearchContext.key === 'containerId';
  const resultNoun = ordersList.length === 1 ? 'Container' : 'Containers';
  const showContainerList = !(isContainerMode && ordersList.length === 1);
  const hasResult = Boolean(orderAttributes);
  const containerListSubtitle =
    isContainerMode && ordersList.length === 1
      ? 'Direct match for this container search.'
      : isContainerMode
        ? 'Matching container records for this search.'
        : 'Grouped under one B/L or booking.';
  const resultSummaryText = isContainerMode
    ? `Result for Container ID ${lastSearchContext.value || '--'}.`
    : `Result for ${lastSearchContext.label} ${lastSearchContext.value || '--'}.`;

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const onSearch = async (e) => {
    e.preventDefault();
    const searchValue = trackingValue.trim();
    const activeSearchMode = mode;

    if (!searchValue) {
      setErrorText('Please enter a tracking value before searching.');
      return;
    }

    setLoading(true);
    setErrorText('');
    setResponseData(null);
    setSelectedOrderId('');
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const params = new URLSearchParams({
        platform: TRACKING_PLATFORM,
        tracking: searchValue,
        key: mode.key,
      });

      const requestUrl = `${TRACKING_API}?${params.toString()}`;
      const response = await fetch(requestUrl, { signal: controller.signal });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      const orderCount = data?.orders?.data?.length || 0;

      if (!orderCount) {
        setErrorText(
          'No shipment found. Please check your value and search type.',
        );
      } else {
        setResponseData(data);
        setSelectedOrderId(data?.orders?.data?.[0]?.id || '');
        setLastSearchContext({
          key: activeSearchMode.key,
          label: activeSearchMode.label,
          value: searchValue,
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setErrorText(
        'Tracking service is currently unavailable. Please try again shortly.',
      );
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Fragment>
      <HeaderTop />
      <Navbar hclass={'wpo-site-header'} Logo={Logo} />
      <PageTitle pageTitle={'Shipment Visibility'} pagesub={'Tracking'} />

      <section className='track-page section-padding'>
        <div className='container'>
          <div
            className={
              hasResult
                ? 'tracking-shell has-result'
                : 'tracking-shell no-result'
            }
          >
            <div className='search-hero'>
              <h2>Track Your Shipment</h2>
              <p>
                Search result shows all matching containers, then opens detail
                for the selected one.
              </p>

              <form onSubmit={onSearch}>
                <div className='tabs'>
                  {SEARCH_MODES.map((searchMode) => (
                    <button
                      key={searchMode.key}
                      type='button'
                      className={
                        searchMode.key === mode.key ? 'tab active' : 'tab'
                      }
                      onClick={() => setMode(searchMode)}
                    >
                      {searchMode.label}
                    </button>
                  ))}
                </div>

                <div className='search-row'>
                  <input
                    className='input'
                    type='text'
                    value={trackingValue}
                    onChange={(e) => setTrackingValue(e.target.value)}
                    placeholder={mode.placeholder}
                  />
                  <button type='submit' className='btn' disabled={loading}>
                    {loading ? 'Searching...' : 'Track Shipment'}
                  </button>
                </div>
              </form>
            </div>

            {errorText && (
              <div className='tracking-alert error'>{errorText}</div>
            )}
            {loading && (
              <div
                className='tracking-alert info'
                style={{ marginBottom: '20px' }}
              >
                Fetching latest shipment details...
              </div>
            )}

            {!hasResult && (
              <div className='pre-result-section'>
                <div className='pre-result-copy'>
                  <span className='label'>Before You Search</span>
                  <h3>Track Faster With Correct References</h3>
                  <p className='muted'>
                    Use the same format your shipping team receives from carrier
                    updates. For fastest match, start with Container ID, then
                    use B/L or Booking if needed.
                  </p>
                  <div className='tips-grid'>
                    <div className='tip-card'>
                      <strong>Container ID</strong>
                      <p className='muted'>
                        Best for direct one-container lookup and latest event.
                      </p>
                    </div>
                    <div className='tip-card'>
                      <strong>Bill of Lading</strong>
                      <p className='muted'>
                        Returns grouped containers under one shipment.
                      </p>
                    </div>
                    <div className='tip-card'>
                      <strong>Booking Number</strong>
                      <p className='muted'>
                        Use when B/L is not yet issued by carrier.
                      </p>
                    </div>
                  </div>
                </div>

                <div className='pre-result-images'>
                  <div className='image-card large'>
                    <img src={TransportBg} alt='Cargo vessel route overview' />
                  </div>
                  <div className='image-card'>
                    <img src={TextBg} alt='Container logistics process' />
                  </div>
                </div>
              </div>
            )}

            {hasResult && (
              <div className='result-area'>
                <div className='master-grid'>
                  <div className='master-summary'>
                    <span className='label'>Search Result</span>
                    <h3>
                      {ordersList.length} {resultNoun} Found
                    </h3>
                    <p className='muted'>
                      {resultSummaryText}{' '}
                      {ordersList.length > 1
                        ? 'Select a container to view milestone, route, cargo, and terminal events.'
                        : 'Detailed shipment milestones are shown below.'}
                    </p>
                  </div>

                  <div className='route-card'>
                    <div>
                      <span className='label'>POL</span>
                      <h4>{orderAttributes.origin || '--'}</h4>
                      <p className='route-time'>
                        Departure: {formatDate(orderAttributes.receiptDate)}
                      </p>
                    </div>
                    <div className='route-arrow'>-&gt;</div>
                    <div>
                      <span className='label'>POD</span>
                      <h4>{orderAttributes.destination || '--'}</h4>
                      <p className='route-time'>
                        Arrival: {formatDate(orderAttributes.deliveryDate)}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={
                    showContainerList
                      ? 'split-layout'
                      : 'split-layout single-column'
                  }
                >
                  {showContainerList && (
                    <div className='result-card'>
                      <div className='section-head'>
                        <div>
                          <h3>Containers</h3>
                          <p className='muted'>{containerListSubtitle}</p>
                        </div>
                      </div>
                      <div className='container-list'>
                        {ordersList.map((orderItem) => {
                          const attr = orderItem.attributes || {};
                          const itemTimeline = normalizeTimeline(
                            attr.trackingDetails || [],
                          );
                          const itemLastEvent =
                            itemTimeline[itemTimeline.length - 1];
                          const itemStatus = getShipmentStatus(
                            attr,
                            itemLastEvent,
                          );
                          const itemProgress = getProgress(
                            itemStatus,
                            itemTimeline.length,
                            itemLastEvent?.location,
                            attr.destination,
                          );
                          const isActive = activeOrder?.id === orderItem.id;

                          return (
                            <button
                              key={orderItem.id}
                              type='button'
                              className={
                                isActive
                                  ? 'container-item active'
                                  : 'container-item'
                              }
                              onClick={() => {
                                setSelectedOrderId(orderItem.id);
                              }}
                            >
                              <div className='container-item-top'>
                                <strong>{attr.containerId || '--'}</strong>
                                <span
                                  className={`status-badge ${getStatusTone(
                                    itemStatus,
                                    itemLastEvent?.location,
                                    attr.destination,
                                  )}`}
                                >
                                  {itemStatus}
                                </span>
                              </div>
                              <p className='muted'>
                                {attr.size || '--'} | {attr.sealNo || '--'}
                              </p>
                              <div className='progress-track'>
                                <div
                                  className='progress-fill'
                                  style={{ width: `${itemProgress}%` }}
                                />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className='result-card'>
                    <div className='section-head'>
                      <div>
                        <span className='label'>Selected Container</span>
                        <h3>{orderAttributes.containerId || '--'}</h3>
                        <p className='muted'>
                          {orderAttributes.size || '--'} | Seal{' '}
                          {orderAttributes.sealNo || '--'} | Gross Weight{' '}
                          {orderAttributes.weight || '--'}
                        </p>
                      </div>
                      <span
                        className={`status-badge ${getStatusTone(
                          activeStatus,
                          latestEvent?.location,
                          orderAttributes?.destination,
                        )}`}
                      >
                        {activeStatus}
                      </span>
                    </div>

                    <div className='selected-progress'>
                      <div className='selected-progress-row'>
                        <span className='label'>Progress</span>
                        <strong>{activeProgress}%</strong>
                      </div>
                      <div className='progress-track'>
                        <div
                          className='progress-fill'
                          style={{ width: `${activeProgress}%` }}
                        />
                      </div>
                    </div>

                    <div className='detail-grid'>
                      {/* <div className='mini-card'>
                        <span className='label'>Current Location</span>
                        <strong>{latestEvent?.location || '--'}</strong>
                        <p className='muted'>Last carrier update</p>
                      </div> */}
                      <div className='mini-card'>
                        <span className='label'>ETA</span>
                        <strong>
                          {formatDate(orderAttributes.deliveryDate)}
                        </strong>
                        <p className='muted'>
                          {orderAttributes.destination || '--'}
                        </p>
                      </div>
                      <div className='mini-card'>
                        <span className='label'>Vessel</span>
                        <strong>{orderAttributes.vessel || '--'}</strong>
                        <p className='muted'>
                          {orderAttributes.voyage
                            ? `Voyage ${orderAttributes.voyage}`
                            : '--'}
                        </p>
                      </div>
                    </div>

                    {/* <button
                      type='button'
                      className='link-toggle'
                      onClick={() => setShowMoreDetails((prev) => !prev)}
                    >
                      {showMoreDetails ? 'Hide details' : 'More details'}
                    </button>

                    {showMoreDetails && (
                      <div className='extra-details'>
                        <div className='extra-detail-item'>
                          <span className='label'>Commodity</span>
                          <strong>{orderAttributes.measurement || '--'}</strong>
                        </div>
                        <div className='extra-detail-item'>
                          <span className='label'>Booking</span>
                          <strong>{orderAttributes.bookingNo || '--'}</strong>
                        </div>
                      </div>
                    )} */}

                    <div className='journey'>
                      {!!timeline.length && (
                        <div className='journey-head'>
                          <span className='label'>Journey</span>
                        </div>
                      )}

                      {!timeline.length && (
                        <p className='empty-history'>
                          No tracking milestones available.
                        </p>
                      )}

                      {!!timeline.length &&
                        timeline.map((eventItem) => {
                          const eventVessel =
                            eventItem.vessel ||
                            eventItem.vesselName ||
                            eventItem.shipName ||
                            '';
                          const eventVoyage =
                            eventItem.voyage ||
                            eventItem.voyageNo ||
                            eventItem.voyageNumber ||
                            '';
                          const isLastEvent =
                            eventItem === timeline[timeline.length - 1];
                          const isPending = isPendingMilestone(
                            eventItem.date,
                            eventItem.event,
                          );
                          const isCompleted = isCompletedMilestone(
                            eventItem.event,
                            eventItem.location,
                            orderAttributes?.destination,
                          );
                          const isCurrent =
                            isLastEvent && !isCompleted && !isPending;
                          const dotClass = isPending
                            ? 'dot pending'
                            : isCurrent
                              ? 'dot current'
                              : 'dot';

                          return (
                            <div
                              key={
                                eventItem._id ||
                                `${eventItem.event}-${eventItem.date}`
                              }
                              className='journey-step'
                            >
                              <div className={dotClass}>
                                {isPending ? '…' : isCurrent ? '•' : '✓'}
                              </div>
                              <div>
                                <strong>
                                  {eventItem.event || 'Status update'}
                                </strong>
                                <p className='muted'>
                                  {eventItem.location || '--'}
                                  {eventItem.subLocation
                                    ? `, ${eventItem.subLocation}`
                                    : ''}
                                </p>
                                {(eventVessel || eventVoyage) && (
                                  <p className='muted'>
                                    {eventVessel || '--'}
                                    {eventVoyage
                                      ? ` | Voyage ${eventVoyage}`
                                      : ''}
                                  </p>
                                )}
                              </div>
                              <div className='date'>
                                {formatDate(eventItem.date)}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <MapSection />
      <CtaSection hclass={'wpo-cta-section-s2'} />
      <FooterS2 />
      <Scrollbar />
    </Fragment>
  );
};

export default TrackingPage;
